import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderKanban, Plus, Search, Filter, Clock, CheckCircle2, AlertTriangle,
  ArrowUpRight, Calendar, Users, FileText, MoreVertical, ChevronRight,
  Target, Loader2, Archive, TrendingUp, Pause, XCircle, UserCheck,
  LayoutTemplate, Globe, Smartphone, Brain, Shield, Megaphone, Folder, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { getPermissions } from '../../utils/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getHeaders = () => {
  const token = localStorage.getItem('d3_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/20', dot: 'bg-zinc-400' },
  in_progress: { label: 'In Progress', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', dot: 'bg-blue-400' },
  on_track: { label: 'On Track', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', dot: 'bg-emerald-400' },
  at_risk: { label: 'At Risk', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', dot: 'bg-amber-400' },
  blocked: { label: 'Blocked', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', dot: 'bg-red-400' },
  awaiting_client: { label: 'Awaiting Client', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', dot: 'bg-purple-400' },
  on_hold: { label: 'On Hold', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20', dot: 'bg-orange-400' },
  completed: { label: 'Completed', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', dot: 'bg-emerald-400' },
  cancelled: { label: 'Cancelled', color: 'text-zinc-500', bg: 'bg-zinc-500/10 border-zinc-500/20', dot: 'bg-zinc-500' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${cfg.bg} ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <span className="text-xs text-zinc-500 font-medium uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const ProjectCard = ({ project, onClick }) => {
  const currentPhase = project.phases?.find(p => p.id === project.currentPhaseId);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate group-hover:text-indigo-300 transition-colors">{project.name}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            {project.serviceType && <p className="text-xs text-zinc-500">{project.serviceType}</p>}
            {project.templateName && <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">Template</span>}
          </div>
        </div>
        <StatusBadge status={project.status} />
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
          <span className="flex items-center gap-1">Progress
            {project.progressMode === 'manual' && <span className="text-amber-400">(Manual)</span>}
          </span>
          <span>{project.progressPercent || 0}%</span>
        </div>
        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${project.progressPercent || 0}%` }}
            className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full"
          />
        </div>
      </div>

      <div className="flex items-center gap-4 text-[11px] text-zinc-500">
        {currentPhase && (
          <span className="flex items-center gap-1">
            <Target className="w-3 h-3" /> {currentPhase.name}
          </span>
        )}
        {project.targetDate && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {new Date(project.targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
        {project.clientName && (
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" /> {project.clientName}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <span className="text-[10px] text-zinc-600">
          Updated {new Date(project.lastUpdate || project.updatedAt).toLocaleDateString()}
        </span>
        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-indigo-400 transition-colors" />
      </div>
    </motion.div>
  );
};

// ── Create Project Modal ───────────────────────────────────
const CreateProjectModal = ({ isOpen, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', description: '', serviceType: '', clientName: '', targetDate: '', startDate: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(form) });
      if (res.ok) {
        const proj = await res.json();
        toast.success('Project created');
        onCreated(proj);
        onClose();
        setForm({ name: '', description: '', serviceType: '', clientName: '', targetDate: '', startDate: '' });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create project');
      }
    } catch { toast.error('Connection error'); }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-lg p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="create-project-modal"
      >
        <h2 className="text-lg font-semibold text-white mb-4">Create New Project</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Project Name *</label>
            <input
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
              placeholder="e.g. Enterprise Platform Redesign" required
              data-testid="project-name-input"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Description</label>
            <textarea
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40 resize-none"
              rows={3} placeholder="Brief project description..."
              data-testid="project-desc-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Service Type</label>
              <input
                value={form.serviceType} onChange={e => setForm(f => ({ ...f, serviceType: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                placeholder="e.g. Software Dev"
                data-testid="project-service-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Client Name</label>
              <input
                value={form.clientName} onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                placeholder="e.g. Acme Corp"
                data-testid="project-client-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/40"
                data-testid="project-start-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Target Date</label>
              <input type="date" value={form.targetDate} onChange={e => setForm(f => ({ ...f, targetDate: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/40"
                data-testid="project-target-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="cancel-create-btn">
              Cancel
            </button>
            <button type="submit" disabled={loading || !form.name.trim()}
              className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-40 transition-colors flex items-center gap-2"
              data-testid="submit-create-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Project
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ── Template Icon Map ──────────────────────────────────────
const TEMPLATE_ICONS = {
  globe: Globe, smartphone: Smartphone, brain: Brain,
  shield: Shield, megaphone: Megaphone, folder: Folder,
};

// ── Template Selection Modal ──────────────────────────────
const TemplateSelectionModal = ({ isOpen, onClose, onCreated }) => {
  const [step, setStep] = useState('browse'); // browse | configure
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState({ name: '', clientName: '', serviceType: '', startDate: '', targetDate: '', description: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep('browse');
    setSelectedTemplate(null);
    setForm({ name: '', clientName: '', serviceType: '', startDate: '', targetDate: '', description: '' });
    (async () => {
      try {
        const [tRes, cRes] = await Promise.all([
          fetch(`${API_URL}/api/templates`, { headers: getHeaders() }),
          fetch(`${API_URL}/api/templates/categories`, { headers: getHeaders() }),
        ]);
        if (tRes.ok) setTemplates(await tRes.json());
        if (cRes.ok) setCategories(await cRes.json());
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [isOpen]);

  const filtered = selectedCategory === 'all' ? templates : templates.filter(t => t.category === selectedCategory);

  const handleSelectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setForm(f => ({
      ...f,
      serviceType: tmpl.category || '',
      description: tmpl.description || '',
    }));
    setStep('configure');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !selectedTemplate) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/create-project`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ templateId: selectedTemplate.id, ...form }),
      });
      if (res.ok) {
        const proj = await res.json();
        toast.success(`Project created from "${selectedTemplate.name}" template`);
        onCreated(proj);
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create project');
      }
    } catch { toast.error('Connection error'); }
    setCreating(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="template-selection-modal"
      >
        {step === 'browse' ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-semibold text-white">Create from Template</h2>
                <p className="text-xs text-zinc-500 mt-1">Select a template to jumpstart your project with predefined phases, milestones, and deliverables</p>
              </div>
              <button onClick={onClose} className="text-zinc-500 hover:text-white p-1.5 rounded-lg hover:bg-white/5" data-testid="close-template-modal">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Category filters */}
            <div className="flex gap-1.5 mb-4 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  selectedCategory === 'all' ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5 border border-transparent'
                }`}
                data-testid="template-category-all"
              >All</button>
              {categories.map(c => (
                <button key={c.name} onClick={() => setSelectedCategory(c.name)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    selectedCategory === c.name ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5 border border-transparent'
                  }`}
                  data-testid={`template-category-${c.name}`}
                >{c.name}</button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
                {filtered.map(tmpl => {
                  const Icon = TEMPLATE_ICONS[tmpl.icon] || LayoutTemplate;
                  return (
                    <button key={tmpl.id} onClick={() => handleSelectTemplate(tmpl)}
                      className="text-left bg-white/[0.02] border border-white/10 rounded-xl p-4 hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-all group"
                      data-testid={`template-card-${tmpl.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colors">{tmpl.name}</h3>
                          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{tmpl.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-600">
                            <span>{tmpl.phases?.length || 0} phases</span>
                            <span>{tmpl.milestones?.length || 0} milestones</span>
                            <span>{tmpl.deliverables?.length || 0} deliverables</span>
                            <span>{tmpl.estimatedDurationDays}d</span>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-zinc-700 group-hover:text-indigo-400 transition-colors shrink-0 mt-1" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <button onClick={() => setStep('browse')} className="text-zinc-500 hover:text-white text-sm" data-testid="back-to-templates">
                &larr; Back
              </button>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white">Configure Project</h2>
                <p className="text-xs text-zinc-500 mt-0.5">Based on: <span className="text-indigo-400">{selectedTemplate?.name}</span></p>
              </div>
            </div>

            {/* Template summary */}
            <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-3 mb-5 flex items-center gap-4 text-xs text-indigo-300">
              <span>{selectedTemplate?.phases?.length} phases</span>
              <span className="text-indigo-500/40">|</span>
              <span>{selectedTemplate?.milestones?.length} milestones</span>
              <span className="text-indigo-500/40">|</span>
              <span>{selectedTemplate?.deliverables?.length} deliverables</span>
              <span className="text-indigo-500/40">|</span>
              <span>~{selectedTemplate?.estimatedDurationDays} days</span>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Project Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                  placeholder="e.g. Acme Corp Website Redesign" required data-testid="template-project-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Client Name</label>
                  <input value={form.clientName} onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                    placeholder="e.g. Acme Corp" data-testid="template-client-name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Service Type</label>
                  <input value={form.serviceType} onChange={e => setForm(f => ({ ...f, serviceType: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                    placeholder="Auto-filled from template" data-testid="template-service-type"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Start Date</label>
                  <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/40"
                    data-testid="template-start-date"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Target Date</label>
                  <input type="date" value={form.targetDate} onChange={e => setForm(f => ({ ...f, targetDate: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/40"
                    data-testid="template-target-date"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="cancel-template-btn">Cancel</button>
                <button type="submit" disabled={creating || !form.name.trim()}
                  className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-40 transition-colors flex items-center gap-2"
                  data-testid="create-from-template-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LayoutTemplate className="w-4 h-4" />}
                  Create from Template
                </button>
              </div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ── Main Projects Screen ───────────────────────────────────
export default function ProjectsScreen({ onOpenProject }) {
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [projRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/projects`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/projects/dashboard/stats`, { headers: getHeaders() }),
      ]);
      if (projRes.ok) setProjects(await projRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = projects.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.clientName?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'active') return ['in_progress', 'on_track'].includes(p.status);
    if (filter === 'attention') return ['at_risk', 'blocked', 'awaiting_client'].includes(p.status);
    if (filter === 'completed') return p.status === 'completed';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">My Projects</h1>
          <p className="text-sm text-zinc-500 mt-1">Track progress, milestones, and deliverables</p>
        </div>
        {perms.canCreateProject && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTemplates(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white text-sm font-medium rounded-xl transition-colors border border-white/10"
              data-testid="create-from-template-btn"
            >
              <LayoutTemplate className="w-4 h-4" /> From Template
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors shadow-[0_0_20px_rgba(79,70,229,0.2)]"
              data-testid="create-project-btn"
            >
              <Plus className="w-4 h-4" /> New Project
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={FolderKanban} label="Total" value={stats.totalProjects} color="bg-indigo-500/10 text-indigo-400" />
          <StatCard icon={TrendingUp} label="Active" value={stats.active} color="bg-blue-500/10 text-blue-400" />
          <StatCard icon={CheckCircle2} label="On Track" value={stats.onTrack} color="bg-emerald-500/10 text-emerald-400" />
          <StatCard icon={AlertTriangle} label="Needs Attention" value={stats.atRisk} color="bg-amber-500/10 text-amber-400" />
          <StatCard icon={UserCheck} label="Awaiting Client" value={stats.awaitingClient} color="bg-purple-500/10 text-purple-400" />
          <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} color="bg-emerald-500/10 text-emerald-400" />
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search projects..."
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/30"
            data-testid="search-projects-input"
          />
        </div>
        <div className="flex gap-1.5">
          {[
            { id: 'all', label: 'All' },
            { id: 'active', label: 'Active' },
            { id: 'attention', label: 'Needs Attention' },
            { id: 'completed', label: 'Completed' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                filter === f.id ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <ProjectCard key={p.id} project={p} onClick={() => onOpenProject(p.id)} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-600">
          <FolderKanban className="w-12 h-12 mb-4 text-zinc-700" />
          <h3 className="text-lg font-medium text-white mb-1">
            {projects.length === 0 ? 'No projects yet' : 'No matching projects'}
          </h3>
          <p className="text-sm text-zinc-500 mb-4">
            {projects.length === 0 ? 'Create your first project to get started' : 'Try adjusting your filters'}
          </p>
          {projects.length === 0 && perms.canCreateProject && (
            <button onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors"
              data-testid="empty-create-btn"
            >
              <Plus className="w-4 h-4 inline mr-1" /> Create Project
            </button>
          )}
        </div>
      )}

      {/* Create Modal */}
      <CreateProjectModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(p) => { setProjects(prev => [p, ...prev]); fetchData(); }}
      />

      {/* Template Selection Modal */}
      <TemplateSelectionModal
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onCreated={(p) => { setProjects(prev => [p, ...prev]); fetchData(); }}
      />
    </div>
  );
}
